import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import api from '../../services/api';
import Input from '../ui/Input';
import Button from '../ui/Button';
import { useAuth } from '../../context/AuthContext';
import { trackEvent } from '../../utils/analytics';
import { Sparkles } from 'lucide-react';

const Register: React.FC = () => {
    const [formData, setFormData] = useState({
        fullName: '',
        email: '',
        password: '',
        phone: ''
    });
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const navigate = useNavigate();
    const location = useLocation();
    const { login } = useAuth();
    const redirectTo =
        typeof (location.state as { from?: string } | null)?.from === 'string'
            ? (location.state as { from: string }).from
            : '/espace';

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');
        try {
            const { data } = await api.post('/auth/register', formData);
            login(data.user, data.token);
            trackEvent('sign_up', { method: 'email' });
            navigate(redirectTo.startsWith('/') ? redirectTo : '/espace', { replace: true });
        } catch (err: any) {
            setError(err.response?.data?.error || err.message || "Une erreur est survenue lors de l'inscription");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center px-4 py-12 relative overflow-hidden">
            {/* Background Decorations */}
            <div className="absolute top-[-10%] right-[-10%] w-[40%] h-[40%] bg-indigo-200/20 blur-[120px] rounded-full"></div>
            <div className="absolute bottom-[-10%] left-[-10%] w-[40%] h-[40%] bg-emerald-200/20 blur-[120px] rounded-full"></div>

            <div className="max-w-md w-full animate-fade-in">
                <div className="text-center mb-10">
                    <div className="inline-flex items-center justify-center p-2 bg-emerald-500 rounded-2xl shadow-xl shadow-emerald-100 mb-6 transition-transform hover:scale-110">
                        <img src="/flashcv.png" alt="FlashCv Logo" className="w-10 h-10 object-contain" />
                    </div>
                    <h2 className="text-4xl font-extrabold text-slate-900 tracking-tight mb-2">Créer un compte</h2>
                    <p className="text-slate-500 font-medium">Commencez à créer votre CV professionnel</p>
                </div>

                <div className="card-premium p-8 sm:p-10 glass-morphism">
                    <form className="space-y-4" onSubmit={handleSubmit}>
                        <Input
                            label="Nom Complet"
                            name="fullName"
                            placeholder="Jean Dupont"
                            required
                            value={formData.fullName}
                            onChange={handleChange}
                        />
                        <Input
                            label="Email"
                            type="email"
                            name="email"
                            placeholder="jean@exemple.com"
                            required
                            value={formData.email}
                            onChange={handleChange}
                        />
                        <Input
                            label="Téléphone"
                            name="phone"
                            placeholder="+225 01 02 03 04 05"
                            value={formData.phone}
                            onChange={handleChange}
                        />
                        <Input
                            label="Mot de passe"
                            type="password"
                            name="password"
                            placeholder="••••••••"
                            required
                            value={formData.password}
                            onChange={handleChange}
                        />

                        {error && (
                            <div className="p-3 bg-red-50 border border-red-100 rounded-xl text-red-600 text-sm text-center font-medium animate-fade-in">
                                {error}
                            </div>
                        )}

                        <Button
                            type="submit"
                            variant="secondary"
                            className="w-full text-lg h-12 mt-6"
                            isLoading={isLoading}
                        >
                            <Sparkles className="w-5 h-5 mr-2" />
                            S'inscrire gratuitement
                        </Button>

                        <div className="pt-4 text-center border-t border-slate-100">
                            <p className="text-slate-500 text-sm">
                                Déjà membre ?{' '}
                                <Link
                                    to="/login"
                                    state={{ from: redirectTo !== '/espace' ? redirectTo : undefined }}
                                    className="font-bold text-indigo-600 hover:text-indigo-700 transition-colors"
                                >
                                    Se connecter
                                </Link>
                            </p>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default Register;
